import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import DropdownSelect from '../../Generic/DropdownSelect';
import { getSupplierCredits } from '../../../clients/supplier_credit_client';
import { logInfo, logWarn } from '../../../config_and_helpers/logger';
import { mapExtractedQuoteToCreatePayload } from '../../../utils/quoteExtractionMapping';
import {
  isEncoQuoteFormat,
  validateExtractedQuoteForSave,
} from '../../../utils/quoteExtractionValidation';
import BudgetLineSelect from '../../LabData/BudgetLineSelect';
import EditableQuoteHeaderForm from './EditableQuoteHeaderForm';
import EditableQuoteItemsTable from './EditableQuoteItemsTable';
import ExtractionWarnings from './ExtractionWarnings';
import ExtractionDebugPanel from './ExtractionDebugPanel';
import './QuoteExtractionPreview.css';

const LOG_CONTEXT = 'QuoteExtractionPreview';

/**
 * @param {Object} props
 * @param {Object} props.extractedData
 * @param {string} [props.token]
 * @param {Array} props.supplierOptions
 * @param {Array} props.projectOptions
 * @param {Array} props.userOptions
 * @param {number|null} props.selectedProject
 * @param {number|null} props.selectedUser
 * @param {Function} props.onProjectChange
 * @param {Function} props.onUserChange
 * @param {string|number|null} props.supplierId
 * @param {Function} props.onSupplierChange
 * @param {Function} props.onSave
 * @param {Function} props.onCancel
 * @param {boolean} props.isSaving
 * @param {string} props.saveError
 * @param {{ formatId: string, displayName: string }|null} [props.resolvedFormat]
 * @param {{ value: unknown, label: string, matchReason: string }|null} [props.suggestedSupplier]
 * @param {Array} [props.formatWarnings]
 * @param {boolean} props.disabled
 */
const QuoteExtractionPreview = ({
  extractedData,
  token = '',
  resolvedFormat = null,
  suggestedSupplier = null,
  formatWarnings = [],
  supplierOptions = [],
  projectOptions = [],
  userOptions = [],
  selectedProject = null,
  selectedUser = null,
  onProjectChange,
  onUserChange,
  supplierId = '',
  onSupplierChange,
  onSave,
  onCancel,
  isSaving = false,
  saveError = '',
  disabled = false,
}) => {
  const [draft, setDraft] = useState(extractedData);
  const [budgetLineId, setBudgetLineId] = useState(null);
  const [budget, setBudget] = useState('');
  const [supplierCredits, setSupplierCredits] = useState([]);
  const [isLoadingSupplierCredits, setIsLoadingSupplierCredits] = useState(false);
  const [fundingCreditId, setFundingCreditId] = useState('');

  const isEncoFormat = useMemo(() => isEncoQuoteFormat(draft), [draft]);

  useEffect(() => {
    if (!supplierId || !token) {
      setSupplierCredits([]);
      setIsLoadingSupplierCredits(false);
      return;
    }
    let isCancelled = false;
    setIsLoadingSupplierCredits(true);
    logInfo(LOG_CONTEXT, 'Supplier credits fetch start', {
      supplierId: String(supplierId),
    });
    getSupplierCredits(token, supplierId)
      .then(credits => {
        if (isCancelled) return;
        const list = Array.isArray(credits) ? credits : [];
        setSupplierCredits(list);
        logInfo(LOG_CONTEXT, 'Supplier credits fetch success', {
          supplierId: String(supplierId),
          count: list.length,
        });
      })
      .catch(error => {
        if (isCancelled) return;
        setSupplierCredits([]);
        logWarn(LOG_CONTEXT, 'Supplier credits fetch failed', {
          supplierId: String(supplierId),
          message: error?.message || 'unknown error',
        });
      })
      .finally(() => {
        if (!isCancelled) setIsLoadingSupplierCredits(false);
      });
    return () => {
      isCancelled = true;
    };
  }, [supplierId, token]);

  const validation = useMemo(
    () =>
      validateExtractedQuoteForSave(draft, {
        supplierId,
        supplierOptions,
        budgetLineId,
        budget,
        fundingCreditId,
      }),
    [draft, supplierId, supplierOptions, budgetLineId, budget, fundingCreditId]
  );

  const displayWarnings = useMemo(() => {
    const seen = new Set();
    return [...formatWarnings, ...validation.warnings].filter(w => {
      const key = `${w.field || ''}:${w.message}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [formatWarnings, validation.warnings]);

  const handleSave = () => {
    if (!validation.canSave) return;
    const payload = mapExtractedQuoteToCreatePayload(draft, {
      supplierId,
      projectId: selectedProject,
      userId: selectedUser,
      budgetLineId,
      budget: String(budget || '').trim() || '',
      fundingCreditId:
        String(fundingCreditId || '').trim() !== '' ? fundingCreditId : null,
      suggestedSupplier,
      resolvedFormat,
    });
    onSave(payload, draft);
  };

  const handleSupplierChange = nextSupplierId => {
    setFundingCreditId('');
    setBudgetLineId(null);
    onSupplierChange(nextSupplierId);
  };

  const formatDisplayName =
    resolvedFormat?.displayName ||
    (draft.meta?.supplierFormat ? String(draft.meta.supplierFormat) : null);

  return (
    <div className="quote-extraction-preview mt-3">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <h6 className="mb-0">Review extracted quote</h6>
        {formatDisplayName && (
          <Badge bg="primary">Detected format: {formatDisplayName}</Badge>
        )}
        {draft.meta?.sourceFileName && (
          <span className="small text-muted">{draft.meta.sourceFileName}</span>
        )}
      </div>

      <p className="small text-muted">
        Extraction is a draft — correct any missing or wrong values before saving.
      </p>

      {suggestedSupplier && (
        <Alert variant="info" className="py-2 small">
          Matched Materiah supplier: <strong>{suggestedSupplier.label}</strong>
          {suggestedSupplier.matchReason
            ? ` (${suggestedSupplier.matchReason})`
            : ''}
          . You can change the supplier below if needed.
        </Alert>
      )}

      <ExtractionWarnings warnings={displayWarnings} />

      {validation.blockingErrors.length > 0 && (
        <Alert variant="danger" className="py-2 small">
          <ul className="mb-0 ps-3">
            {validation.blockingErrors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </Alert>
      )}

      <div className="mb-3">
        <DropdownSelect
          label="Supplier (required)"
          optionsList={supplierOptions}
          selectedValue={
            (supplierOptions || []).find(
              o => String(o.value) === String(supplierId)
            ) || null
          }
          setSelectedValue={opt =>
            handleSupplierChange(opt?.value != null ? opt.value : '')
          }
          disabled={disabled || isSaving}
          placeholder="Select supplier"
        />
        <div className="row mt-2">
          <div className="col-md-6">
            <DropdownSelect
              label="Project (optional)"
              optionsList={projectOptions}
              selectedValue={
                (projectOptions || []).find(
                  o => String(o.value) === String(selectedProject)
                ) || null
              }
              setSelectedValue={opt =>
                onProjectChange(opt?.value != null ? Number(opt.value) : null)
              }
              disabled={disabled || isSaving}
              placeholder="No project"
              clearable
            />
          </div>
          <div className="col-md-6">
            <DropdownSelect
              label="User (optional)"
              optionsList={userOptions}
              selectedValue={
                (userOptions || []).find(
                  o => String(o.value) === String(selectedUser)
                ) || null
              }
              setSelectedValue={opt =>
                onUserChange(opt?.value != null ? Number(opt.value) : null)
              }
              disabled={disabled || isSaving}
              placeholder="No user"
              clearable
            />
          </div>
        </div>
      </div>

      <Row className="g-3 mb-3">
        <Col md={12}>
          <div className="border rounded p-2 bg-light">
            <div className="small text-muted mb-2">
              Funding source <span className="text-danger ms-1">*</span>{' '}
              <span>(Must choose or enter one)</span>
            </div>
            <Row className="g-2">
              <Col md={6}>
                <BudgetLineSelect
                  token={token}
                  label="Budget"
                  required
                  value={budgetLineId}
                  onChange={id => {
                    setBudgetLineId(id);
                    if (id) {
                      setBudget('');
                      setFundingCreditId('');
                    }
                  }}
                  disabled={disabled || isSaving}
                />
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Supplier credit</Form.Label>
                  {!supplierId ? (
                    <Form.Text className="text-muted small d-block">
                      Select a supplier to load available credits.
                    </Form.Text>
                  ) : isLoadingSupplierCredits ? (
                    <div className="d-flex align-items-center">
                      <Spinner animation="border" size="sm" className="me-2" />
                      <span className="small text-muted">Loading credits…</span>
                    </div>
                  ) : (
                    <Form.Select
                      value={fundingCreditId}
                      onChange={e => setFundingCreditId(e.target.value || '')}
                      disabled={disabled || isSaving}
                    >
                      <option value="">None / use budget amount</option>
                      {(supplierCredits || []).map(credit => (
                        <option key={credit.id} value={credit.id}>
                          {credit.name || `Credit #${credit.id}`} - remaining{' '}
                          {credit.remaining_amount} {credit.currency}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>

      <EditableQuoteHeaderForm
        data={draft}
        onChange={setDraft}
        disabled={disabled || isSaving}
        showEncoFields={isEncoFormat}
      />

      <hr />

      <EditableQuoteItemsTable
        items={draft.items || []}
        onChange={items => setDraft(prev => ({ ...prev, items }))}
        disabled={disabled || isSaving}
        isEncoFormat={isEncoFormat}
      />

      <ExtractionDebugPanel extractedData={extractedData} editedSnapshot={draft} />

      {saveError && (
        <Alert variant="danger" className="mt-3 py-2 small">
          {saveError}
        </Alert>
      )}

      <div className="d-flex gap-2 mt-3">
        <Button
          variant="primary"
          type="button"
          disabled={disabled || isSaving || !validation.canSave}
          onClick={handleSave}
        >
          {isSaving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving…
            </>
          ) : (
            'Save quote'
          )}
        </Button>
        <Button
          variant="outline-secondary"
          type="button"
          disabled={isSaving}
          onClick={onCancel}
        >
          Back
        </Button>
      </div>
    </div>
  );
};

export default QuoteExtractionPreview;
